/* ── Hitsilistan järjestys (T641, Toni 20.10.2026 klo 16.39) ─────────────────────────────
   Yksi yhteinen järjestys jokaiselle hitsilistalle: palvelin antaa rivit tuonti- tai luontijärjestyksessä, ja jokainen lista
   järjestää ne tällä funktiolla eikä sivukohtaisesti.
     1. lehti: 1/2 ennen 2/2, numerot lukuina; tyhjä lehti («-» tai puuttuva) viimeisenä
     2. sen sisällä hitsinumero luonnollisena lukujärjestyksenä: 2, 2.1, 3, 10, 25, 25.1, 25.2 (kantanumero ja alinumero lukuina,
        sama sääntö kuin palvelimen hitsinumeroJarjestys, api/src/hitsinumero.ts)
     3. uusintahitsi (R) heti alkuperäisensä perään (korjaa_hitsi_id); R, jonka alkuperäinen ei ole listalla, omalla numerollaan
     4. numerottomat ja paikanpitäjät («(suunniteltu)», «00», «0», «?») listan loppuun
   Tasatilanteessa id, jotta järjestys on sama joka latauksella. */

export interface JarjestettavaHitsi {
  id: number;
  hitsinumero: string;
  piirustuslehti?: string | null;
  hitsityyppi?: string | null;
  korjaa_hitsi_id?: number | null;
  numero_tarkastettava?: boolean;
}

const NUMERO = /^\s*(\d+)(?:[.,](\d+))?\s*$/;

function numero(h: JarjestettavaHitsi): [number, number] | null {
  if (h.numero_tarkastettava) return null;
  const m = NUMERO.exec(h.hitsinumero ?? "");
  if (!m) return null;
  const kanta = Number(m[1]);
  return kanta === 0 ? null : [kanta, m[2] === undefined ? -1 : Number(m[2])];
}

function lehtiTyhja(l: string | null | undefined): boolean {
  const s = (l ?? "").trim();
  return s === "" || s === "-";
}

function vertaa(a: JarjestettavaHitsi, b: JarjestettavaHitsi): number {
  const na = numero(a), nb = numero(b);
  if (!na !== !nb) return na ? -1 : 1;
  const ta = lehtiTyhja(a.piirustuslehti), tb = lehtiTyhja(b.piirustuslehti);
  if (ta !== tb) return ta ? 1 : -1;
  const lehti = (a.piirustuslehti ?? "").localeCompare(b.piirustuslehti ?? "", "fi", { numeric: true });
  if (lehti) return lehti;
  if (na && nb) {
    if (na[0] !== nb[0]) return na[0] - nb[0];
    if (na[1] !== nb[1]) return na[1] - nb[1];
  } else {
    const t = (a.hitsinumero ?? "").localeCompare(b.hitsinumero ?? "", "fi", { numeric: true });
    if (t) return t;
  }
  return a.id - b.id;
}

export function jarjestaHitsit<T extends JarjestettavaHitsi>(rivit: readonly T[]): T[] {
  const idt = new Set(rivit.map((h) => h.id));
  const korjaukset = new Map<number, T[]>();
  const runko: T[] = [];
  for (const h of rivit) {
    if (h.hitsityyppi === "R" && h.korjaa_hitsi_id && h.korjaa_hitsi_id !== h.id && idt.has(h.korjaa_hitsi_id)) {
      const l = korjaukset.get(h.korjaa_hitsi_id) ?? [];
      l.push(h);
      korjaukset.set(h.korjaa_hitsi_id, l);
    } else runko.push(h);
  }
  const tulos: T[] = [];
  const lisaa = (h: T) => {
    tulos.push(h);
    for (const r of (korjaukset.get(h.id) ?? []).sort((a, b) => a.id - b.id)) lisaa(r);
  };
  for (const h of runko.sort(vertaa)) lisaa(h);
  return tulos;
}
